import * as React from 'react';
import { Grid, makeStyles, Typography, Box } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { Tabs, Tab } from '@swagup-com/components';
import { useRouter } from 'next/router';
import FontFaceObserver from 'fontfaceobserver';
import { useHoliday } from '../../contexts/holiday';
import { useAuth } from '../global/Authentication/AuthProvider';
import { LoggedOut, LoggedIn } from './SessionHandler';
import HeaderMenu from './Menu';
import { Link } from '../shared';
import {
  productMenuItemList,
  companyMenuItemList,
  showDashboard,
  packsMenuItemList,
  platformMenuItemList,
  useTabsPathsMap
} from './common';
import ClickableTooltip from './ClickableTooltip';
import { getFieldFromCookie } from '../../helpers/utilsCookies';
import { InvalidShipmentNotification } from '../icons/shipments';
import useHasInvalidAddress from '../../hooks/useHasInvalidAddress';

const isPathInList = list => path => list.find(p => path.startsWith(p));

const headerTabsPaths = [
  {
    value: '/inventory',
    pathMatch: isPathInList([
      '/inventory',
      '/reorder/',
      '/empty-inventory',
      '/shipping',
      '/shipping-order-confirmation/',
      '/send-swag-review',
      '/send-swag?product'
    ])
  },
  {
    value: '/orders',
    pathMatch: isPathInList([
      '/orders',
      '/orders-requested',
      '/orders-completed',
      '/order-details/',
      '/reorder/',
      '/proof-details',
      '/shipping',
      '/shipping-order-confirmation/'
    ])
  },
  {
    value: '/shipments',
    pathMatch: isPathInList([
      '/shipments',
      '/add-employee',
      '/contact-details/',
      '/send-swag',
      '/add-contact',
      '/file-review'
    ])
  },
  {
    value: '/products',
    pathMatch: isPathInList([
      '/products',
      '/product-details',
      '/product-onboarding',
      '/bulk',
      '/custom-swag-packs',
      '/preset-pack'
    ])
  },
  {
    value: '/',
    pathMatch: path => ['/'].includes(path)
  },
  {
    value: '/redeem-pages',
    pathMatch: isPathInList(['/redeem-pages', '/redeem-details', '/redeem-pages-create', '/redeem-history'])
  }
];

const commonTabStyle = { fontFamily: 'Gilroy-SemiBold', opacity: 1 };

const useTabStyles = makeStyles({
  root: {
    justifyContent: 'center',
    height: 36.5,
    '& .MuiTab-labelIcon': {
      minHeight: 'auto',
      paddingTop: 6
    }
  },
  scroller: {
    flexGrow: '0'
  },
  tab: {
    paddingLeft: 19,
    paddingRight: 19,
    color: '#434D5C',
    ...commonTabStyle
  }
});

// const showNewBadge = label => ['Redeem', 'Products'].includes(label);

const DashboardSection = ({ pathname }) => {
  const [tabIndex, setTabIndex] = React.useState(0);
  const haveInvalidAddress = useSelector(state => state.shipments.haveInvalidAddress);
  const tabsActions = React.useRef(null);
  useHasInvalidAddress();
  const activeTabs = useTabsPathsMap();
  const classes = useTabStyles();

  React.useEffect(() => {
    new FontFaceObserver('Gilroy-SemiBold')
      .load(null, 5000)
      .then(() => tabsActions.current && tabsActions.current.updateIndicator());
  }, []);

  React.useEffect(() => {
    const tabsPaths = Object.values(activeTabs);
    const tabPath = headerTabsPaths.find(t => t.pathMatch(pathname))?.value;
    const index = tabsPaths.indexOf(tabsPaths.find(t => t.startsWith(tabPath)));
    setTabIndex(index === -1 ? false : index);
  }, [activeTabs, pathname, tabIndex]);

  const handleIcon = label =>
    label === 'Shipments' && haveInvalidAddress ? <InvalidShipmentNotification /> : undefined;

  // const handleLabel = label => (showNewBadge(label) ? <NewBadge size="small">{label}</NewBadge> : label);

  return (
    <Tabs
      action={tabsActions}
      classes={{ root: classes.root, scroller: classes.scroller }}
      variant="scrollable"
      scrollButtons="on"
      value={tabIndex}
    >
      {Object.entries(activeTabs).map(([label, path]) => (
        <Tab
          key={label}
          style={{ paddingLeft: 19, paddingRight: 19, color: '#434D5C', ...commonTabStyle }}
          label={label}
          component={Link}
          to={path}
          icon={handleIcon(label)}
          className={classes.tab}
        />
      ))}
    </Tabs>
  );
};

const useStyles = makeStyles({
  text: {
    color: ({ darkMode }) => (darkMode ? '#ffffff' : '#434D5C'),
    fontFamily: 'Gilroy-SemiBold',
    cursor: 'pointer'
  }
});

const tabStyle = darkMode => ({
  paddingLeft: 13,
  paddingRight: 13,
  color: darkMode ? '#ffffff' : '#434D5C',
  ...commonTabStyle
});

const getHeaderItems = () => {
  const productsMenuItems =
    getFieldFromCookie('affiliate_id') === 'postal.io' ? packsMenuItemList : productMenuItemList;
  return [
    { title: 'Products', menuList: productsMenuItems },
    { title: 'Platform', menuList: platformMenuItemList },
    { title: 'Company', menuList: companyMenuItemList },
    { title: 'Pricing', to: '/pricing', absolute: true }
  ];
};

const HeaderItems = () => {
  const darkMode = useHoliday();
  const classes = useStyles({ darkMode });
  const headerItems = getHeaderItems();

  return (
    <Tabs value={false} variant="scrollable" scrollButtons="auto">
      {headerItems.map(({ title, menuList, to, absolute }) =>
        to ? (
          <Tab key={title} style={tabStyle(darkMode)} label={title} component={Link} to={to} absolute={absolute} />
        ) : (
          <Tab
            key={title}
            style={tabStyle(darkMode)}
            label={
              <ClickableTooltip title={<HeaderMenu menuItemList={menuList} />}>
                <Typography component="div" className={classes.text}>
                  <Box fontWeight="fontWeightBold">{title}</Box>
                </Typography>
              </ClickableTooltip>
            }
          />
        )
      )}
    </Tabs>
  );
};

const RegularSection = () => (
  <Grid container alignItems="center" justifyContent="space-between">
    <Grid item md={10} style={{ paddingLeft: 24 }}>
      <HeaderItems />
    </Grid>
  </Grid>
);

const DesktopHeaderContent = ({ pathname, isDashboardHeader }) => {
  const { isPending, isAuthenticated } = useAuth();

  if (!isPending && isAuthenticated && isDashboardHeader && showDashboard(pathname)) {
    return <DashboardSection pathname={pathname} />;
  }
  if (!isPending || !isDashboardHeader || !showDashboard(pathname)) {
    return <RegularSection />;
  }
  return null;
};

const useLandingStyles = makeStyles(theme => ({
  textPhoneHeader: {
    fontFamily: 'Gilroy',
    fontSize: 16,
    fontWeight: 'bold',
    color: '#434c5f',
    display: 'inline-flex',
    width: '100%',
    lineHeight: 1.6,
    '& a': {
      color: '#434c5f'
    },
    [theme.breakpoints.down('sm')]: { fontSize: 12 }
  },
  imageContainerPhoneHeader: {
    height: 25,
    width: 'auto',
    marginRight: 16.5,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      top: 0,
      marginRight: 6,
      height: 18
    }
  }
}));

const LandingHeader = () => {
  const classes = useLandingStyles();

  return (
    <div className={classes.textPhoneHeader}>
      <img className={classes.imageContainerPhoneHeader} src="/images/public/phone-black.svg" alt="Black phone" />
      <a href="tel:6468020884">646-802-0884</a>
    </div>
  );
};

const RegularHeader = ({ authenticated, isPending, isAuthenticated, isDashboardHeader }) => (
  <>
    {authenticated && <LoggedIn isDashboardHeader={isDashboardHeader} />}
    {!isPending && !isAuthenticated && <LoggedOut />}
  </>
);

const DesktopHeader = ({ pathname, isDashboardHeader }) => {
  const { isPending, isAuthenticated } = useAuth();
  const isSSR = useRouter();
  const fromLandingPages = !isSSR && !isAuthenticated && window.location.pathname === '/dashboard/paid-onboarding';

  const authenticated = !isPending && isAuthenticated;
  return (
    <Grid container alignItems="center">
      <Grid item xs={9} lg={10}>
        {!fromLandingPages && <DesktopHeaderContent pathname={pathname} isDashboardHeader={isDashboardHeader} />}
      </Grid>
      <Grid item container justifyContent="flex-end" alignItems="center" xs={3} lg={2}>
        {fromLandingPages ? (
          <LandingHeader />
        ) : (
          <RegularHeader
            authenticated={authenticated}
            isPending={isPending}
            isAuthenticated={isAuthenticated}
            isDashboardHeader={isDashboardHeader}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default DesktopHeader;
